import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CheckCircle2,
  Loader2,
  Trash2,
  Pencil,
  Plus,
  X,
  FileText,
  Users,
} from "lucide-react";
import {
  agents as agentsApi,
  taskTemplates as templatesApi,
} from "../lib/api";
import type {
  Agent,
  TaskTemplate,
  AgentRole,
  TaskType,
} from "../lib/api";

const statusColors: Record<string, string> = {
  idle: "bg-green-400",
  working: "bg-blue-400 animate-pulse",
  offline: "bg-gray-500",
};

const taskTypeLabels: Record<string, string> = {
  "keyword-research": "Keyword Research",
  "content-generation": "Content Generation",
  "site-audit": "Site Audit",
  strategy: "Strategy",
  "site-scrape": "Site Import",
  "competitor-analysis": "Competitor Analysis",
  "link-check": "Link Check",
  "meta-optimization": "Meta Optimization",
  "build-website": "Build Website",
  "site-deploy": "Deploy to Cloudflare",
  "dns-update": "Update DNS",
  "qc-review": "Quality Review",
  "image-generation": "Image Generation",
};

const AI_MODELS = [
  { id: "claude-sonnet-4-20250514", label: "Claude Sonnet 4" },
  { id: "claude-opus-4-20250514", label: "Claude Opus 4" },
  { id: "claude-haiku-4-5-20251001", label: "Claude Haiku 4.5" },
];

type TabId = "agents" | "templates";

export default function AIStudio() {
  // Agents
  const [agentList, setAgentList] = useState<Agent[]>([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [agentForm, setAgentForm] = useState({ name: "", role: "worker" as AgentRole, systemPrompt: "", defaultModel: "" });

  // Templates
  const [templateList, setTemplateList] = useState<TaskTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TaskTemplate | null>(null);
  const [templateForm, setTemplateForm] = useState({
    name: "",
    type: "keyword-research" as TaskType,
    objective: "",
    promptTemplate: "",
    timeoutSeconds: 120,
    maxRetries: 1,
    cronExpression: "" as string,
  });

  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabId>(
    searchParams.get("tab") === "templates" ? "templates" : "agents"
  );

  useEffect(() => {
    loadAgents();
    loadTemplates();
  }, []);

  async function loadAgents() {
    setAgentsLoading(true);
    try {
      const res = await agentsApi.list();
      setAgentList(res.data);
    } catch {}
    setAgentsLoading(false);
  }

  async function loadTemplates() {
    setTemplatesLoading(true);
    try {
      const res = await templatesApi.list();
      setTemplateList(res.data);
    } catch {}
    setTemplatesLoading(false);
  }

  // Agent CRUD
  async function handleSaveAgent() {
    try {
      if (editingAgent) {
        await agentsApi.update(editingAgent._id, agentForm as Partial<Agent>);
      } else {
        await agentsApi.create(agentForm as Partial<Agent>);
      }
      setShowAgentModal(false);
      setEditingAgent(null);
      await loadAgents();
    } catch {}
  }

  async function handleDeleteAgent(id: string) {
    try {
      await agentsApi.delete(id);
      await loadAgents();
    } catch {}
  }

  function openAgentModal(agent?: Agent) {
    if (agent) {
      setEditingAgent(agent);
      setAgentForm({ name: agent.name, role: agent.role, systemPrompt: agent.systemPrompt, defaultModel: agent.defaultModel ?? "" });
    } else {
      setEditingAgent(null);
      setAgentForm({ name: "", role: "worker", systemPrompt: "", defaultModel: "" });
    }
    setShowAgentModal(true);
  }

  // Template CRUD
  async function handleSaveTemplate() {
    try {
      if (editingTemplate) {
        await templatesApi.update(editingTemplate._id, templateForm as Partial<TaskTemplate>);
      } else {
        await templatesApi.create(templateForm as Partial<TaskTemplate>);
      }
      setShowTemplateModal(false);
      setEditingTemplate(null);
      await loadTemplates();
    } catch {}
  }

  async function handleDeleteTemplate(id: string) {
    try {
      await templatesApi.delete(id);
      await loadTemplates();
    } catch {}
  }

  function openTemplateModal(template?: TaskTemplate) {
    if (template) {
      setEditingTemplate(template);
      setTemplateForm({
        name: template.name,
        type: template.type,
        objective: template.objective,
        promptTemplate: template.promptTemplate,
        timeoutSeconds: template.timeoutSeconds,
        maxRetries: template.maxRetries,
        cronExpression: template.cronExpression ?? "",
      });
    } else {
      setEditingTemplate(null);
      setTemplateForm({ name: "", type: "keyword-research", objective: "", promptTemplate: "", timeoutSeconds: 120, maxRetries: 1, cronExpression: "" });
    }
    setShowTemplateModal(true);
  }

  const tabs: { id: TabId; label: string; icon: React.ReactNode }[] = [
    { id: "agents", label: "Agents", icon: <Users className="w-4 h-4" /> },
    { id: "templates", label: "Processes", icon: <FileText className="w-4 h-4" /> },
  ];

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-gray-100">AI Agents</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Manage agents and processes. Enable processes per website to automate recurring tasks.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-800">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? "border-brand-500 text-brand-400"
                : "border-transparent text-gray-500 hover:text-gray-300"
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* ─── AGENTS TAB ─── */}
      {activeTab === "agents" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-100">Agents</h2>
            <button
              onClick={() => openAgentModal()}
              className="flex items-center gap-1 px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white text-xs rounded-lg transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> New Agent
            </button>
          </div>

          {agentsLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-gray-600 animate-spin" /></div>
          ) : agentList.length === 0 ? (
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center">
              <Users className="w-8 h-8 text-gray-700 mx-auto mb-2" />
              <p className="text-sm text-gray-600">No agents configured. Create one to get started.</p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {agentList.map((agent) => (
                <div key={agent._id} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-2.5 h-2.5 rounded-full ${statusColors[agent.status]}`} />
                      <h3 className="text-sm font-medium text-gray-100">{agent.name}</h3>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => openAgentModal(agent)} className="p-1 text-gray-500 hover:text-gray-300 transition-colors">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleDeleteAgent(agent._id)} className="p-1 text-gray-500 hover:text-red-400 transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-gray-500">
                      Role: <span className="text-gray-300 capitalize">{agent.role.replace("_", " ")}</span>
                    </p>
                    <p className="text-xs text-gray-500">
                      Status: <span className="text-gray-300 capitalize">{agent.status}</span>
                    </p>
                    {agent.defaultModel && (
                      <p className="text-xs text-gray-500">
                        Model: <span className="text-gray-300">{agent.defaultModel}</span>
                      </p>
                    )}
                  </div>
                  <p className="mt-2 text-xs text-gray-600 line-clamp-2">{agent.systemPrompt}</p>
                </div>
              ))}
            </div>
          )}

          {/* Agent Modal */}
          {showAgentModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
              <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 w-full max-w-lg space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-100">
                    {editingAgent ? "Edit Agent" : "Create Agent"}
                  </h3>
                  <button onClick={() => setShowAgentModal(false)} className="text-gray-500 hover:text-gray-300">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Name</label>
                    <input
                      value={agentForm.name}
                      onChange={(e) => setAgentForm((f) => ({ ...f, name: e.target.value }))}
                      className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Role</label>
                    <select
                      value={agentForm.role}
                      onChange={(e) => setAgentForm((f) => ({ ...f, role: e.target.value as AgentRole }))}
                      className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    >
                      <option value="worker">Worker</option>
                      <option value="orchestrator">Orchestrator</option>
                      <option value="quality_control">Quality Control</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">System Prompt</label>
                    <textarea
                      value={agentForm.systemPrompt}
                      onChange={(e) => setAgentForm((f) => ({ ...f, systemPrompt: e.target.value }))}
                      rows={5}
                      className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-brand-500 resize-y"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Default Model</label>
                    <select
                      value={agentForm.defaultModel}
                      onChange={(e) => setAgentForm((f) => ({ ...f, defaultModel: e.target.value }))}
                      className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    >
                      <option value="">Use default</option>
                      {AI_MODELS.map((m) => (
                        <option key={m.id} value={m.id}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <button onClick={() => setShowAgentModal(false)} className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 text-xs rounded-lg">
                    Cancel
                  </button>
                  <button onClick={handleSaveAgent} className="px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white text-xs rounded-lg">
                    {editingAgent ? "Update" : "Create"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── TEMPLATES TAB ─── */}
      {activeTab === "templates" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-100">Processes</h2>
            <button
              onClick={() => openTemplateModal()}
              className="flex items-center gap-1 px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white text-xs rounded-lg transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> New Process
            </button>
          </div>

          {templatesLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-gray-600 animate-spin" /></div>
          ) : templateList.length === 0 ? (
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center">
              <FileText className="w-8 h-8 text-gray-700 mx-auto mb-2" />
              <p className="text-sm text-gray-600">No processes configured. Create one to automate recurring tasks.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {templateList.map((tmpl) => (
                <div key={tmpl._id} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="text-sm font-medium text-gray-100">{tmpl.name}</h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {taskTypeLabels[tmpl.type] ?? tmpl.type}
                        {tmpl.cronExpression ? ` | Recurring` : ` | One-time`}
                        {tmpl.model && ` | ${tmpl.model}`}
                        {` | Timeout: ${tmpl.timeoutSeconds}s`}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => openTemplateModal(tmpl)} className="p-1 text-gray-500 hover:text-gray-300 transition-colors">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleDeleteTemplate(tmpl._id)} className="p-1 text-gray-500 hover:text-red-400 transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400">{tmpl.objective}</p>
                  {tmpl.qcChecklist && tmpl.qcChecklist.length > 0 && (
                    <div className="mt-2 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3 text-green-500" />
                      <span className="text-xs text-gray-500">{tmpl.qcChecklist.length} QC checks</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Template Modal */}
          {showTemplateModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
              <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 w-full max-w-lg space-y-4 max-h-[80vh] overflow-y-auto">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-100">
                    {editingTemplate ? "Edit Process" : "Create Process"}
                  </h3>
                  <button onClick={() => setShowTemplateModal(false)} className="text-gray-500 hover:text-gray-300">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Name</label>
                    <input
                      value={templateForm.name}
                      onChange={(e) => setTemplateForm((f) => ({ ...f, name: e.target.value }))}
                      className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Type</label>
                    <select
                      value={templateForm.type}
                      onChange={(e) => setTemplateForm((f) => ({ ...f, type: e.target.value as TaskType }))}
                      className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    >
                      {Object.entries(taskTypeLabels).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Objective</label>
                    <input
                      value={templateForm.objective}
                      onChange={(e) => setTemplateForm((f) => ({ ...f, objective: e.target.value }))}
                      className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Prompt Template <span className="text-gray-600">(use {"{{variable}}"} for placeholders)</span>
                    </label>
                    <textarea
                      value={templateForm.promptTemplate}
                      onChange={(e) => setTemplateForm((f) => ({ ...f, promptTemplate: e.target.value }))}
                      rows={6}
                      className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-brand-500 resize-y font-mono"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Timeout (seconds)</label>
                      <input
                        type="number"
                        value={templateForm.timeoutSeconds}
                        onChange={(e) => setTemplateForm((f) => ({ ...f, timeoutSeconds: parseInt(e.target.value) || 120 }))}
                        className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Max Retries</label>
                      <input
                        type="number"
                        value={templateForm.maxRetries}
                        onChange={(e) => setTemplateForm((f) => ({ ...f, maxRetries: parseInt(e.target.value) || 1 }))}
                        className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Frequency</label>
                    <select
                      value={templateForm.cronExpression}
                      onChange={(e) => setTemplateForm((f) => ({ ...f, cronExpression: e.target.value }))}
                      className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    >
                      <option value="">One-time (no schedule)</option>
                      <optgroup label="Per week">
                        <option value="0 9 * * 1">1x per week</option>
                        <option value="0 9 * * 1,4">2x per week</option>
                        <option value="0 9 * * 1,3,5">3x per week</option>
                        <option value="0 9 * * 1,2,3,4,5">5x per week (weekdays)</option>
                      </optgroup>
                      <optgroup label="Every X days">
                        <option value="0 9 * * *">Every day</option>
                        <option value="0 9 */2 * *">Every 2 days</option>
                        <option value="0 9 */3 * *">Every 3 days</option>
                        <option value="0 9 */5 * *">Every 5 days</option>
                        <option value="0 9 */7 * *">Every 7 days</option>
                        <option value="0 9 */10 * *">Every 10 days</option>
                        <option value="0 9 */14 * *">Every 14 days</option>
                      </optgroup>
                      <optgroup label="Monthly">
                        <option value="0 9 1 * *">Monthly (1st)</option>
                        <option value="0 9 1,15 * *">Twice a month (1st & 15th)</option>
                      </optgroup>
                    </select>
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <button onClick={() => setShowTemplateModal(false)} className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 text-xs rounded-lg">
                    Cancel
                  </button>
                  <button onClick={handleSaveTemplate} className="px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white text-xs rounded-lg">
                    {editingTemplate ? "Update" : "Create"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
